import { costMicros } from '@shared/pricing'
import { type ToolResult, type TranscriptEntry, type UserBlock } from '@shared/session'
import { clsx } from 'clsx'
import { ChevronDown, FileText, X } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { IconButton } from '@/components/Button'
import { useI18n } from '@/i18n/i18n'
import { useChatVariant } from './chatLayout'
import { formatUsd } from './describe'

// The trace layer: exactly what the model was given and did, entry by entry.
// Nothing here is shown by default; it opens from a turn's "過程" entry.

function Pre({ children, tone }: { children: string; tone?: 'error' | 'muted' }) {
  const [open, setOpen] = useState(children.length < 1200)
  return (
    <div className="relative">
      <pre
        className={clsx(
          'overflow-x-auto rounded-md border px-2.5 py-2 font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]',
          tone === 'error'
            ? 'border-danger/30 bg-danger/5 text-danger'
            : 'border-line bg-surface-2 text-ink-2',
          !open && 'max-h-48 overflow-hidden',
        )}
      >
        {children}
      </pre>
      {!open && (
        <button
          type="button"
          onClick={() => {
            setOpen(true)
          }}
          className="absolute inset-x-0 bottom-0 flex h-10 items-end justify-center rounded-b-md bg-linear-to-b from-transparent to-surface-2 pb-1 text-[11.5px] text-ai"
        >
          <ChevronDown size={13} />
        </button>
      )}
    </div>
  )
}

function Section({
  label,
  meta,
  children,
}: {
  label: string
  meta?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="space-y-1.5">
      <header className="flex items-baseline gap-2 text-[11px] font-semibold tracking-[0.04em] text-muted uppercase">
        {label}
        {meta && <span className="font-normal tracking-normal normal-case">{meta}</span>}
      </header>
      {children}
    </section>
  )
}

function UserBlocks({ blocks }: { blocks: UserBlock[] }) {
  return (
    <div className="space-y-1.5">
      {blocks.map((b, i) =>
        b.type === 'text' ? (
          <Pre key={i}>{b.text}</Pre>
        ) : (
          <div
            key={i}
            className="inline-flex items-center gap-1.5 rounded-md border border-line px-2 py-1 text-[12px]"
          >
            <FileText size={13} className="text-muted" />
            {b.name}
            <span className="text-muted">{b.mime}</span>
          </div>
        ),
      )}
    </div>
  )
}

type Block = { type: string; [k: string]: unknown }

/**
 * A tool call's input, readable: short values on one line each, text values
 * (card markdown, what was said) as text rather than escaped JSON.
 */
/** Inputs that are prose or markdown: always shown as text. */
const TEXT_FIELDS = new Set(['text', 'markdown', 'old_text', 'new_text'])

function ToolInput({ input }: { input: unknown }) {
  if (!input || typeof input !== 'object' || Array.isArray(input))
    return <Pre>{JSON.stringify(input, null, 2)}</Pre>
  const entries = Object.entries(input as Record<string, unknown>)
  const long = entries.filter(
    ([k, v]) => typeof v === 'string' && (TEXT_FIELDS.has(k) || v.includes('\n') || v.length > 80),
  )
  const short = entries.filter((e) => !long.includes(e))
  return (
    <div className="space-y-1.5">
      {short.length > 0 && (
        <Pre>{short.map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join('\n')}</Pre>
      )}
      {long.map(([k, v]) => (
        <div key={k}>
          <div className="mb-0.5 font-mono text-[11px] text-muted">{k}</div>
          <Pre>{v as string}</Pre>
        </div>
      ))}
    </div>
  )
}

function EntryView({
  e,
  results,
  calls,
}: {
  e: TranscriptEntry
  /** Tool results by tool_use id, shown under the call that produced them. */
  results: Map<string, ToolResult>
  /** Ids of the tool calls on screen. */
  calls: Set<string>
}) {
  const { t } = useI18n()
  switch (e.kind) {
    case 'turn_start':
      return null
    case 'user':
      return (
        <Section label={t('trace.input')}>
          <UserBlocks blocks={e.content} />
        </Section>
      )
    case 'assistant': {
      const cost = costMicros(e.model, e.usage)
      const u = e.usage
      return (
        <Section
          label={t('trace.model')}
          meta={`${e.model} · ${e.effort} · in ${u.input_tokens + (u.cache_creation_input_tokens ?? 0) + (u.cache_read_input_tokens ?? 0)} (cache ${u.cache_read_input_tokens ?? 0}) · out ${u.output_tokens}${cost !== null ? ` · ${formatUsd(cost)}` : ''}`}
        >
          {(e.content as Block[]).map((b, i) => {
            if (b.type === 'thinking')
              return (
                <div
                  key={i}
                  className="text-[12.5px] leading-relaxed text-muted italic whitespace-pre-wrap"
                >
                  {typeof b.thinking === 'string' ? b.thinking : ''}
                </div>
              )
            if (b.type === 'redacted_thinking')
              return (
                <div key={i} className="text-[12px] text-muted italic">
                  {t('trace.redacted')}
                </div>
              )
            if (b.type === 'text')
              return (
                <div key={i}>
                  <div className="mb-1 text-[11px] text-muted">{t('trace.notes')}</div>
                  <Pre>{typeof b.text === 'string' ? b.text : ''}</Pre>
                </div>
              )
            if (b.type === 'tool_use') {
              const r = results.get(String(b.id))
              return (
                <div key={i} className="space-y-1.5">
                  <div className="font-mono text-[12px] font-semibold text-ai">
                    {String(b.name)}
                  </div>
                  <ToolInput input={b.input} />
                  {r && (
                    <div className="flex gap-1.5">
                      <span className="pt-2 font-mono text-[11px] text-muted">→</span>
                      <div className="min-w-0 flex-1">
                        <Pre {...(r.is_error && { tone: 'error' as const })}>{r.content}</Pre>
                      </div>
                    </div>
                  )}
                </div>
              )
            }
            return <Pre key={i}>{JSON.stringify(b, null, 2)}</Pre>
          })}
        </Section>
      )
    }
    case 'tool_results': {
      // Results are shown under their calls; only orphans (none expected) stay here.
      const orphans = e.results.filter((r) => !calls.has(r.tool_use_id))
      if (!orphans.length) return null
      return (
        <Section label={t('trace.results')}>
          {orphans.map((r) => (
            <Pre key={r.tool_use_id} {...(r.is_error && { tone: 'error' as const })}>
              {r.content}
            </Pre>
          ))}
        </Section>
      )
    }
    case 'notice':
      return (
        <Section label={t('trace.notice')}>
          <Pre tone="muted">{e.text}</Pre>
        </Section>
      )
    case 'turn_end':
      return e.error || e.aborted ? (
        <Section label={t('trace.end')}>
          <Pre tone="error">{e.error ?? t('chat.stopped')}</Pre>
        </Section>
      ) : null
  }
}

export type PanelTab = 'changes' | 'trace'

/**
 * One panel for looking behind a turn (DESIGN.md 信任層): what it changed on
 * the board ("改動", the change view) and what the model was given and did
 * ("過程", the trace). The change view is per turn; the trace can also show
 * the whole conversation.
 */
export function TracePanel({
  entries,
  scope,
  onScope,
  onClose,
  tab = 'trace',
  onTab,
  changes,
}: {
  entries: TranscriptEntry[]
  /** A turn id, or null for the whole session. */
  scope: string | null
  onScope: (scope: string | null) => void
  onClose: () => void
  tab?: PanelTab
  onTab?: (tab: PanelTab) => void
  /** The change view for `scope`, shown on the 改動 tab. */
  changes?: ReactNode
}) {
  const { t } = useI18n()
  const main = useChatVariant() === 'main'
  const shown = scope ? entries.filter((e) => e.turnId === scope) : entries
  const turnIds = [...new Set(entries.map((e) => e.turnId))]
  const results = new Map<string, ToolResult>()
  const calls = new Set<string>()
  for (const e of shown) {
    if (e.kind === 'tool_results') for (const r of e.results) results.set(r.tool_use_id, r)
    if (e.kind === 'assistant')
      for (const b of e.content as Block[]) if (b.type === 'tool_use') calls.add(String(b.id))
  }
  return (
    <div
      role="dialog"
      aria-label={t('trace.title')}
      className={clsx(
        'absolute inset-y-0 z-30 flex min-w-80 flex-col border-l border-line bg-surface shadow-lg',
        // Beside the sidebar over the canvas; over the right of the chat page in chat-main.
        main
          ? 'right-0 w-[min(560px,92%)] max-[899px]:w-full'
          : 'right-full w-[min(560px,calc(100vw-420px))] max-[899px]:right-0 max-[899px]:w-full',
      )}
    >
      <header className="flex h-12 flex-none items-center gap-2 border-b border-line px-3">
        {onTab ? (
          <div role="tablist" className="flex items-center gap-0.5">
            {(['changes', 'trace'] as const).map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={tab === k}
                disabled={k === 'changes' && !changes}
                onClick={() => {
                  onTab(k)
                }}
                className={clsx(
                  'rounded-md px-2 py-1 text-[13.5px] font-semibold disabled:opacity-40',
                  tab === k ? 'text-ink' : 'text-muted hover:text-ink-2',
                )}
              >
                {t(k === 'changes' ? 'change.tab' : 'trace.tab')}
              </button>
            ))}
          </div>
        ) : (
          <h2 className="text-[13.5px] font-semibold">{t('trace.title')}</h2>
        )}
        <div
          className={clsx(
            'ml-2 inline-flex rounded-md bg-surface-2 p-0.5 text-[12px]',
            tab === 'changes' && 'hidden',
          )}
        >
          <button
            type="button"
            aria-pressed={scope !== null}
            disabled={!turnIds.length}
            onClick={() => {
              onScope(scope ?? turnIds.at(-1) ?? null)
            }}
            className={clsx(
              'rounded-[5px] px-2 py-0.5',
              scope !== null ? 'bg-surface text-ink shadow-sm' : 'text-muted',
            )}
          >
            {t('trace.thisTurn')}
          </button>
          <button
            type="button"
            aria-pressed={scope === null}
            onClick={() => {
              onScope(null)
            }}
            className={clsx(
              'rounded-[5px] px-2 py-0.5',
              scope === null ? 'bg-surface text-ink shadow-sm' : 'text-muted',
            )}
          >
            {t('trace.session')}
          </button>
        </div>
        <span className="flex-1" />
        <IconButton label={t('trace.close')} onClick={onClose}>
          <X size={16} />
        </IconButton>
      </header>
      {tab === 'changes' && changes}
      <div
        className={clsx(
          'flex-1 space-y-5 overflow-y-auto px-4 py-4',
          tab === 'changes' && 'hidden',
        )}
      >
        {shown.length === 0 && <p className="text-[13px] text-muted">{t('trace.empty')}</p>}
        {shown.map((e, i) => {
          const newTurn = scope === null && e.kind === 'turn_start' && i > 0
          return (
            <div key={`${e.turnId}:${e.seq}`}>
              {newTurn && <hr className="mb-5 border-line" />}
              <EntryView e={e} results={results} calls={calls} />
            </div>
          )
        })}
      </div>
    </div>
  )
}
